import {
	ITrackingFact,
	ITrackingFactLocation,
	ITrackingUserDetails,
	IUrlParameters,
	ValidFactActions,
	EncodedCharacters,
	UrlParametersKeys
} from '../../@types/global/tracking';
import dataHandler from './TrackingDataHandler';
import specialPartnershipHandler from './TrackingSpecialPartnershipHandler';
import trackingDataHandler from './TrackingDataHandler';

class TrackingOperator {
	// TODO: SP Tracker - Once we get rid of the "TrackingViewsImpressionsHandler" class, remove these two methods.
	public constructFactDetailsForViewsAndImpressions(element: HTMLElement): ITrackingFact | null {
		return this.constructFactDetails(element);
	}

	public constructUserDetailsForViewsAndImpressions(): ITrackingUserDetails {
		return this.constructUserDetails();
	}

	public storePreviousPageAsTrackingDetail(): void {
		const windowName = 'studyportals';
		const initialWindowName = window.name;
		const trackData = dataHandler.retrieveTrackingDetails();
		if (!trackData) {
			return;
		}

		const referrerHost = dataHandler.retrieveHostName(document.referrer);
		const prevPageHost = dataHandler.retrieveHostName(trackData.previousPage);
		const sameReferrerHost = referrerHost && prevPageHost.includes(referrerHost);

		window.name = windowName;

		/**
		 * If there is no referrer, or the host name is different,
		 * then we know a new session has started. We can then reset the
		 * previous page to empty.
		 *
		 * Exception: The user has just landed and then refreshed.
		 * There will still be no referrer in this instance.
		 */

		if (!sameReferrerHost && initialWindowName !== windowName) {
			trackData.previousPage = '';
			dataHandler.storeTrackingDetails(trackData);
		}

		/**
		 * If window name is not set to the name we want, and we have the same
		 * host name coming from the referrer, then we have opened
		 * the page in a new tab, or this is the first time on the page.
		 */

		if (sameReferrerHost && initialWindowName !== windowName) {
			trackData.previousPage = document.referrer;
			dataHandler.storeTrackingDetails(trackData);
		}
	}

	public updateHrefsOfReferralLinks(elements?: HTMLElement[]): void {
		specialPartnershipHandler.prepareGACrossDomainTrackingLinker();

		const referralLinks = elements || dataHandler.retrieveReferralLinks();
		referralLinks.forEach((element) => {
			this.updateHref(element);
		});
	}

	private updateHref(element: HTMLElement): void {
		const href = dataHandler.retrieveElementPropertyHref(element);
		const enrichedHref = this.enrichHrefWithTimestamp(href);

		let url: URL;

		try {
			url = new URL(enrichedHref);
		} catch (e) {
			if(window.Rollbar && e instanceof Error) {
				window.Rollbar.error(e.message, e, { enrichedHref });
				return;
			}

			throw e;
		}

		url = specialPartnershipHandler.applyPartnershipSpecificUrlAdjustments(element, url);

		// TODO: SP Tracker - Once Big Data has made the necessary adjustments, the session & domain ids can be removed.
		let urlParameters: IUrlParameters = {
			target: this.getLinkIdFromElement(element),
			facts: this.encodeTrackingDetailsForHref(element),
			taps: dataHandler.retrieveTrackingDetailsTapGroups(),
			...dataHandler.retrieveSessionAndDomainIds()
		};
		urlParameters = this.addPglParameterToIUrlParameters(urlParameters, element);

		const targetUrlParameter = url.searchParams.get(UrlParametersKeys.TARGET);
		if (targetUrlParameter !== null) {
			urlParameters = this.extractTargetPathAndParamsFromTarget(targetUrlParameter, urlParameters);
		}

		const keysOfUrlParameters = Object.keys(urlParameters) as UrlParametersKeys[];
		keysOfUrlParameters.forEach((key) => {
			url = this.setAsSearchParameterForUrl(url, urlParameters, key);
		});

		element.setAttribute('href', url.toString());
	}

	private extractTargetPathAndParamsFromTarget(targetUrlParameter: string, urlParameters: IUrlParameters): IUrlParameters {
		const targetUrlParameterAsNumber = Number.parseInt(targetUrlParameter, 10);
		if(!Number.isNaN(targetUrlParameterAsNumber)) {
			// If the Target is a number, then it is a linkId and no path or params can be extracted.
			// TODO: CITA-20307 - Rework the entire system that is currently responsible for generating the tracking url.
			return urlParameters;
		}

		const targetUrl = new URL(targetUrlParameter);

		if (targetUrl.searchParams.size > 0) {
			urlParameters.target_params = btoa(targetUrl.searchParams.toString());
		}

		if (targetUrl.pathname !== '/') {
			urlParameters.target_path = btoa(targetUrl.pathname);
		}

		return urlParameters;
	}

	private encodeTrackingDetailsForHref(element: HTMLElement): string {
		const trackingDetails = trackingDataHandler.retrieveTrackingDetails();
		if (!trackingDetails) {
			/* This looks odd, but is necessary to ensure that an url of the
			same format is constructed when no tracking details can be found. */
			return 'null';
		}

		const factDetails = this.constructFactDetails(element);
		const userDetails = this.constructUserDetails();
		const locationDetails = dataHandler.retrieveLocationDetailsBasedOnTrackingLocationElement();
		const details = {
			l: locationDetails,
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			s: dataHandler.retrieveTrackingDetailsSearch(),
			u: userDetails,
			f: [factDetails]
		};

		const data = JSON.stringify(details);
		if (!window.Loot) {
			return data;
		}

		return this.btoa(data);
	}

	private btoa(data: string): string {
		return btoa(JSON.stringify(JSON.parse(data)));
	}

	private constructFactDetails(element: HTMLElement): ITrackingFact | null {
		const fact: ITrackingFact = {
			a: dataHandler.retrieveElementPropertyAction(element),
			t: dataHandler.retrieveElementPropertyType(element),
			i: dataHandler.retrieveElementPropertyId(element),
			d: dataHandler.retrieveElementPropertyDescription(element),
			l: dataHandler.retrieveElementPropertyLabel(element)
		};

		if (!this.areFactDetailsValid(fact)) {
			return null;
		}

		const extra = dataHandler.retrieveElementPropertyExtra(element);
		if (extra) {
			fact.ex = extra;
		}

		const locationDetails = this.constructLocationDetails(element);
		if (locationDetails !== undefined) {
			fact.lo = locationDetails;
		}

		return fact;
	}

	private constructLocationDetails(element: HTMLElement): ITrackingFactLocation | null | undefined {
		if (dataHandler.doesElementHaveNoLocationTrackingMarker(element)) {
			return null;
		} else if (dataHandler.doesParentHaveSameType(element)) {
			return dataHandler.retrieveParentElementBasedLocation(element);
		} else if (dataHandler.doesElementHaveLocationDataProperty(element)) {
			return dataHandler.retrieveLocationBasedOnDataLocationProperty(element);
		} else if (dataHandler.doesFactInfoMatchGenericLocationInfoForView(element)) {
			return null;
		}
	}

	private constructUserDetails(): ITrackingUserDetails {
		return {
			i: dataHandler.retrieveTrackingDetailsIP(),
			s: dataHandler.retrieveTrackingDetailsUUID(),
			l: dataHandler.retrieveTrackingDetailsLanguage(),
			c: dataHandler.retrieveTrackingDetailsNationality(),
			a: dataHandler.retrieveTrackingDetailsUserAgent()
		};
	}

	private enrichHrefWithTimestamp(href: string): string {
		const text = `${EncodedCharacters.HASH}${EncodedCharacters.HASH}timestamp${EncodedCharacters.HASH}${EncodedCharacters.HASH}`;
		if (!href.includes(text)) {
			return href;
		}

		return href.replace(text, Math.round(new Date().getTime() / 1000).toString());
	}

	private setAsSearchParameterForUrl(url: URL, urlParameters: IUrlParameters, key: UrlParametersKeys): URL {
		if (urlParameters[key] !== undefined) {
			url.searchParams.set(key, urlParameters[key] as string);
		}

		return url;
	}

	private areFactDetailsValid(fact: ITrackingFact): boolean {
		const validActions: string[] = Object.values(ValidFactActions);
		return fact.t.length > 0 && fact.i.length > 0 && validActions.includes(fact.a);
	}

	private addPglParameterToIUrlParameters(urlParameters: IUrlParameters, element: HTMLElement): IUrlParameters {
		const pgl = this.isPersistGoogleLinkerEnabled(element);
		if (pgl) {
			urlParameters['pgl'] = pgl;
		}

		return urlParameters;
	}

	private isPersistGoogleLinkerEnabled(element: HTMLElement): boolean | null {
		let result = null;

		if (element.dataset?.pgl) {
			result = element.dataset.pgl === '1';
		}

		return result;
	}

	private getLinkIdFromElement(element: HTMLElement): string {
		return dataHandler.retrieveElementPropertyId(element);
	}
}

export default new TrackingOperator();
